// PMax Brand Exclusions — judgment row, Other Campaign Settings section.
// LLM-drafted verdict (rendered above the collapse boundary by FindingCard) on whether
// non-brand / prospecting PMax campaigns restrict branded traffic. This renderer supplies the
// supporting "campaigns Claude saw" table — each PMax campaign with its campaign-level
// brand-list controls (inclusion vs. exclusion) — for verification.

import type { EvidenceProps } from './registry';

interface BrandControl {
  kind: 'inclusion' | 'exclusion';
  brandListId: string | null;
  brandListName: string;
  memberCount: number;
}

interface CampaignRow {
  id?: string;
  name: string;
  controls: BrandControl[];
}

interface BrandListRow {
  id: string;
  name: string;
  memberCount: number;
}

interface PmaxBrandShape {
  totalPmaxCampaigns: number;
  withExclusionCount: number;
  withoutAnyControlCount: number;
  brandListsOnAccount: BrandListRow[];
  campaigns: CampaignRow[];
}

export const PmaxBrandExclusionsEvidence = ({ evidence }: EvidenceProps) => {
  const e = evidence as unknown as PmaxBrandShape;

  return (
    <div className='evidence'>
      <section className='evidence__section'>
        <h4 className='evidence__section-title'>
          Brand control coverage
          <span className='evidence__section-tag evidence__section-tag--context'>
            {e.totalPmaxCampaigns} PMax campaigns
          </span>
        </h4>
        <div className='audience-coverage'>
          <div className='audience-coverage__cell'>
            <div className='audience-coverage__num'>{e.totalPmaxCampaigns.toLocaleString()}</div>
            <div className='audience-coverage__lbl'>PMax campaigns</div>
          </div>
          <div className='audience-coverage__cell'>
            <div className='audience-coverage__num'>{e.withExclusionCount.toLocaleString()}</div>
            <div className='audience-coverage__lbl'>With brand exclusion</div>
          </div>
          <div className='audience-coverage__cell'>
            <div className='audience-coverage__num'>
              {e.withoutAnyControlCount.toLocaleString()}
            </div>
            <div className='audience-coverage__lbl'>No brand control</div>
          </div>
          <div className='audience-coverage__cell'>
            <div className='audience-coverage__num'>
              {e.brandListsOnAccount.length.toLocaleString()}
            </div>
            <div className='audience-coverage__lbl'>Brand lists on account</div>
          </div>
        </div>
      </section>

      <section className='evidence__section'>
        <h4 className='evidence__section-title'>
          PMax campaigns Claude saw
          <span className='evidence__section-tag evidence__section-tag--context'>
            {e.campaigns.length} campaigns
          </span>
        </h4>
        <table className='evidence__table evidence__table--ai-max-brand'>
          <thead>
            <tr>
              <th>Campaign</th>
              <th>Brand-list control</th>
            </tr>
          </thead>
          <tbody>
            {e.campaigns.map(c => (
              <tr key={c.id ?? c.name}>
                <td>{c.name}</td>
                <td>
                  {c.controls.length === 0 ? (
                    <span className='evidence__inline-note'>No brand-list control</span>
                  ) : (
                    <span className='evidence__modifier-list'>
                      {c.controls.map((ctrl, i) => (
                        <span
                          key={`${ctrl.brandListId ?? ctrl.brandListName}-${i}`}
                          className={`evidence__brand-chip evidence__brand-chip--${ctrl.kind}`}
                          title={`${ctrl.memberCount} brand${ctrl.memberCount === 1 ? '' : 's'}`}
                        >
                          {ctrl.kind === 'exclusion' ? 'Excludes' : 'Includes'} {ctrl.brandListName}
                        </span>
                      ))}
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <p className='evidence__footnote'>
        Performance Max matches broadly across Search, Shopping, YouTube and more, so branded
        queries can reach non-brand campaigns. A brand <strong>exclusion</strong> (a BRANDS list
        applied negatively) keeps them out — typically wanted on prospecting / non-brand campaigns
        so they don't absorb high-intent brand traffic from the dedicated brand campaigns. Note:
        brand exclusions reduce volume, so they're a considered choice, not an automatic one.
      </p>
    </div>
  );
};
